'use client';

/**
 * @fileoverview Provider module for theme provider.
 * Theme is applied synchronously (no useEffect delay) and transitions
 * are briefly suppressed during the switch to prevent a slow colour sweep.
 */
import { createContext, useContext, useEffect, useState } from 'react';

type Theme = 'light' | 'dark';

interface ThemeContextType {
  theme: Theme;
  toggleTheme: () => void;
  setTheme: (theme: Theme) => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

/**
 * Apply the theme class/attribute to <html> synchronously.
 * Also sets CSS `color-scheme` so native browser elements (scrollbars,
 * inputs, date pickers) flip immediately without a React render cycle.
 */
function applyThemeToDOM(nextTheme: Theme) {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  const isDark = nextTheme === 'dark';
  root.classList.toggle('dark', isDark);
  root.setAttribute('data-theme', nextTheme);
  root.style.colorScheme = nextTheme;
}

/**
 * Temporarily disables all CSS transitions on the page for one frame so the
 * theme colours swap instantly instead of animating over 200-300 ms.
 */
function withoutTransitions(callback: () => void) {
  if (typeof document === 'undefined') {
    callback();
    return;
  }
  const root = document.documentElement;
  root.classList.add('no-transitions');
  callback();
  requestAnimationFrame(() => {
    root.classList.remove('no-transitions');
  });
}

export const ThemeProvider = ({ children }: { children: React.ReactNode }) => {
  const [theme, setTheme] = useState<Theme>('light');

  // Initialize theme from DOM/localStorage on client mount after hydration
  useEffect(() => {
    try {
      const saved = localStorage.getItem('theme') as Theme | null;
      const system = window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
      const initialTheme = saved || system;
      if (initialTheme !== 'light') {
        setTheme(initialTheme);
      }
    } catch {
      // ignore localStorage failures
    }
  }, []);

  // Sync across browser tabs.
  useEffect(() => {
    const onStorage = (event: StorageEvent) => {
      if (event.key !== 'theme') return;
      const next = event.newValue === 'dark' ? 'dark' : 'light';
      setTheme(next);
      withoutTransitions(() => applyThemeToDOM(next));
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const setThemeExplicit = (nextTheme: Theme) => {
    // Apply to DOM synchronously — before React re-render — so there is
    // zero perceptible delay between the button click and the colour change.
    withoutTransitions(() => applyThemeToDOM(nextTheme));
    try {
      localStorage.setItem('theme', nextTheme);
    } catch {
      // ignore localStorage failures
    }
    setTheme(nextTheme);
  };

  const toggleTheme = () => {
    const newTheme = theme === 'light' ? 'dark' : 'light';
    setThemeExplicit(newTheme);
  };

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme, setTheme: setThemeExplicit }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) throw new Error('useTheme must be used within ThemeProvider');
  return context;
};
